import { useCallback, useEffect, useImperativeHandle, useRef, useState, type RefObject } from 'react';
import { Box, Button, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { useTheme } from '@mui/material/styles';

/**
 * Sign-here box.
 *
 * Written rather than pulled in: the legacy page loaded signature_pad 1.5.3
 * from a CDN, which is a third-party script on the one screen in the app that
 * produces a legal record, and it bound `mousedown`/`touchstart` separately.
 * Pointer events cover finger, stylus and mouse in one path, which is what
 * matters when half the signatures are drawn with a thumb on a phone in a ute.
 *
 * Two things this must get right and the CDN version did not:
 *
 *   * DEVICE PIXEL RATIO. The canvas backing store is sized to
 *     `clientWidth * devicePixelRatio`. Without it a signature drawn on a 3x
 *     phone screen is stored at a third of the resolution it was drawn at and
 *     comes back as a blur when the office opens the record.
 *
 *   * RESIZE. Rotating the phone resizes the element. The strokes are kept as
 *     points and redrawn, rather than living only in the bitmap, so turning
 *     the phone does not wipe the signature.
 *
 * The exported image is always black on white regardless of the app theme: it
 * is printed onto the SWMS document, not rendered in the UI.
 */

export interface SignaturePadHandle {
    /** A `data:image/png;base64,...` string, or null when nothing is drawn. */
    toDataUrl: () => string | null;
    clear: () => void;
}

interface Point {
    readonly x: number;
    readonly y: number;
}

interface SignaturePadProps {
    readonly handleRef: RefObject<SignaturePadHandle | null>;
    /** Told whenever the pad goes from empty to drawn or back, so Submit can enable. */
    readonly onChange?: (hasInk: boolean) => void;
    readonly height?: number;
}

const INK = '#111111';
const PAPER = '#ffffff';
const LINE_WIDTH = 2.2;

export function SignaturePad({ handleRef, onChange, height = 190 }: SignaturePadProps): React.JSX.Element {
    const theme = useTheme();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    /** Completed strokes plus the one in progress. The source of truth; the bitmap is a view of it. */
    const strokesRef = useRef<Point[][]>([]);
    const drawingRef = useRef(false);
    const [hasInk, setHasInk] = useState(false);

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas === null || ctx === null || ctx === undefined) {
            return;
        }
        const ratio = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
        const width = canvas.clientWidth;
        const tall = canvas.clientHeight;
        if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(tall * ratio)) {
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(tall * ratio);
        }
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.fillStyle = PAPER;
        ctx.fillRect(0, 0, width, tall);

        ctx.strokeStyle = INK;
        ctx.lineWidth = LINE_WIDTH;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (const stroke of strokesRef.current) {
            const first = stroke[0];
            if (first === undefined) {
                continue;
            }
            if (stroke.length === 1) {
                // A single tap is a dot, not nothing — people sign with dots.
                ctx.beginPath();
                ctx.arc(first.x, first.y, LINE_WIDTH / 2, 0, Math.PI * 2);
                ctx.fillStyle = INK;
                ctx.fill();
                continue;
            }
            ctx.beginPath();
            ctx.moveTo(first.x, first.y);
            for (const point of stroke.slice(1)) {
                ctx.lineTo(point.x, point.y);
            }
            ctx.stroke();
        }
    }, []);

    useEffect(() => {
        redraw();
        const observer = new ResizeObserver(() => {
            redraw();
        });
        const canvas = canvasRef.current;
        if (canvas !== null) {
            observer.observe(canvas);
        }
        return () => {
            observer.disconnect();
        };
    }, [redraw]);

    const setInk = useCallback(
        (value: boolean) => {
            setHasInk((was) => {
                if (was !== value) {
                    onChange?.(value);
                }
                return value;
            });
        },
        [onChange],
    );

    useImperativeHandle(
        handleRef,
        () => ({
            toDataUrl: () => {
                const canvas = canvasRef.current;
                if (canvas === null || strokesRef.current.length === 0) {
                    return null;
                }
                return canvas.toDataURL('image/png');
            },
            clear: () => {
                strokesRef.current = [];
                redraw();
                setInk(false);
            },
        }),
        [redraw, setInk],
    );

    function pointFrom(e: React.PointerEvent<HTMLCanvasElement>): Point {
        const rect = e.currentTarget.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    return (
        <Box>
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: hasInk ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                    bgcolor: PAPER,
                    transition: 'border-color 0.15s ease',
                }}
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: `${String(height)}px`,
                        // Without this the browser pans the page instead of
                        // drawing the moment a finger moves.
                        touchAction: 'none',
                        cursor: 'crosshair',
                    }}
                    onPointerDown={(e) => {
                        e.currentTarget.setPointerCapture(e.pointerId);
                        drawingRef.current = true;
                        strokesRef.current = [...strokesRef.current, [pointFrom(e)]];
                        redraw();
                        setInk(true);
                    }}
                    onPointerMove={(e) => {
                        if (!drawingRef.current) {
                            return;
                        }
                        const current = strokesRef.current[strokesRef.current.length - 1];
                        if (current === undefined) {
                            return;
                        }
                        current.push(pointFrom(e));
                        redraw();
                    }}
                    onPointerUp={() => {
                        drawingRef.current = false;
                    }}
                    onPointerCancel={() => {
                        drawingRef.current = false;
                    }}
                />
                {!hasInk ? (
                    <Typography
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            color: theme.palette.grey[500],
                            fontSize: '0.9rem',
                            fontWeight: 600,
                        }}
                    >
                        Sign here
                    </Typography>
                ) : null}
            </Box>
            <Button
                size="small"
                startIcon={<UndoIcon />}
                disabled={!hasInk}
                onClick={() => {
                    handleRef.current?.clear();
                }}
                sx={{ mt: 1 }}
            >
                Clear
            </Button>
        </Box>
    );
}
